import { useState, useCallback, useEffect, memo } from 'react';

import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Grid, useTheme } from '@mui/material';
import {
	APIProvider,
	Map,
	MapCameraChangedEvent,
	MapCameraProps,
	RenderingType,
	useMap,
	useMapsLibrary,
} from '@vis.gl/react-google-maps';

import { Maybe } from '@/types';
import { BffFacility, FacilityBrand, BffBrandType } from '@/types/generated';

import { createSVGMarkerObject, getMarkerIcon } from '../../StandardGoogleMap/StandardGoogleMap.helpers';

import { getGoogleMapStyles } from './CumulativeGoogleMapSection.styles';

export interface IGoogleMapsSection {
	nearByFacilities: BffFacility[];
	facilityBasePath: string;
	isSmallScreen: boolean;
	currentFacilityLatitude: number;
	currentFacilityLongitude: number;
	mobileZoom: Maybe<number>;
	desktopZoom: Maybe<number>;
	facilityBrand: FacilityBrand | BffBrandType;
	themeId: string;
	mapCenter: { latitude: number; longitude: number };
	googleMapId: string;
	googleMapApiKey: string;
	forceZoomOut?: boolean;
	setForceZoomOut?: (forceZoomOut: boolean) => void;
}

const MarkerCluster = ({
	markers,
	facilityBrand,
}: {
	markers: { position: { lat: number; lng: number }; title: string }[];
	facilityBrand: FacilityBrand | BffBrandType;
}) => {
	const theme = useTheme();
	const map = useMap();
	const markerLib = useMapsLibrary('marker');

	useEffect(() => {
		if (!map || !markers.length || !markerLib) return;

		// Create marker instances
		const googleMarkers = markers.map(({ position, title }) => {
			const markerElement = document.createElement('div');
			markerElement.innerHTML = `<img src="${createSVGMarkerObject(
				getMarkerIcon(facilityBrand)
			)}" alt="" style="width: 42px; height: 54px;" />`;

			const marker = new markerLib.AdvancedMarkerElement({
				position,
				title,
				content: markerElement,
				map,
			});
			return marker;
		});

		// Create a MarkerClusterer instance with custom renderer
		const clusterer = new MarkerClusterer({
			map,
			markers: googleMarkers,
			renderer: {
				render: ({ count, position }) => {
					const clusterElement = document.createElement('div');
					clusterElement.innerHTML = `
						<div style="
							background-color: ${theme.palette.primary.main};
							border-radius: 50%;
							width: 32px;
							height: 32px;
							display: flex;
							align-items: center;
							justify-content: center;
							color: ${theme.palette.neutrals.white};
							font-weight: bold;
							font-size: 1rem;
							box-shadow: 0px 0px 0px 8px rgba(24, 79, 180, 0.5);
						">
							${count}
						</div>
					`;

					return new markerLib.AdvancedMarkerElement({
						position,
						content: clusterElement,
					});
				},
			},
		});

		return () => {
			clusterer.setMap(null);
			googleMarkers.forEach((marker) => {
				if (marker instanceof markerLib.AdvancedMarkerElement) {
					marker.map = null;
				}
			});
		};
	}, [map, markers, facilityBrand, markerLib, theme.palette.primary.main, theme.palette.neutrals.white]);

	return null;
};

export default memo(function GoogleMapSection({
	nearByFacilities,
	isSmallScreen,
	mobileZoom,
	desktopZoom,
	facilityBrand,
	mapCenter,
	googleMapId,
	googleMapApiKey,
	forceZoomOut,
	setForceZoomOut,
}: Readonly<IGoogleMapsSection>) {
	const [cameraProps, setCameraProps] = useState<MapCameraProps>({
		zoom: isSmallScreen ? (mobileZoom as number) : (desktopZoom as number),
		center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
	});
	const handleCameraChange = useCallback((event: MapCameraChangedEvent) => setCameraProps(event.detail), []);

	useEffect(() => {
		setCameraProps({
			zoom: isSmallScreen ? (mobileZoom as number) : (desktopZoom as number),
			center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
		});
		if (forceZoomOut && setForceZoomOut) {
			setForceZoomOut(false);
		}
	}, [
		mapCenter.latitude,
		mapCenter.longitude,
		mobileZoom,
		desktopZoom,
		isSmallScreen,
		forceZoomOut,
		setForceZoomOut,
	]);

	return (
		<APIProvider apiKey={googleMapApiKey}>
			<Grid item md={8} data-test-id="google_maps_map_section">
				<Map
					{...cameraProps}
					onCameraChanged={handleCameraChange}
					style={getGoogleMapStyles(isSmallScreen)}
					mapId={googleMapId}
					renderingType={RenderingType.VECTOR}
				>
					<MarkerCluster
						markers={nearByFacilities.map((eachFacility: BffFacility) => ({
							position: {
								lat: eachFacility.location?.latitude ?? 0,
								lng: eachFacility.location?.longitude ?? 0,
							},
							title: eachFacility.name ?? '',
						}))}
						facilityBrand={facilityBrand}
					/>
				</Map>
			</Grid>
		</APIProvider>
	);
});
